import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, input, output, inject, effect, untracked } from '@angular/core';

import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// PrimeNG imports
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FileUploadModule } from 'primeng/fileupload';
import { DatePickerModule } from 'primeng/datepicker';
import { AutoCompleteModule } from 'primeng/autocomplete';

// NgxFileDrop for file upload
import { NgxFileDropModule, NgxFileDropEntry } from 'ngx-file-drop';

// Models and services
import { 
  ProductResponse, 
  CreateProductRequest, 
  UpdateProductRequest,
} from '../../models/product.model';

import { ProductService,
  ProductCategoryService,
  BrandService} from '../../services';
import { RETAIL_LOOKUP_PAGE_SIZE } from '../../constants/retail.constants';
import { buildRetailImageWritePayload } from '../../utils/retail-image.util';
import { ToastMessageService, TaxGroupService } from '@fitness-force/api';

// Enums
import { OwnershipLevel } from '@fitness-force/enum';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ImageCropDialogComponent } from '@fitness-force/common/crop';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

// Attribute Schema Types
interface AttributeSchema {
  name: string;
  type: 'enum' | 'boolean' | 'string' | 'number' | 'date';
  required: boolean;
  allowed_values?: string[];
  label?: string;
  display_order: number;
}

@Component({
  selector: 'lib-product-form',
  standalone: true,
  providers: [DialogService],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    ToggleButtonModule,
    ToggleSwitchModule,
    FileUploadModule,
    NgxFileDropModule,
    DatePickerModule,
    AutoCompleteModule
],
  template: `
    <p-dialog
      [visible]="visible()"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '600px' }"
      (onHide)="onCancel.emit()">
    
      <ng-template pTemplate="header">
        <h3>{{ isEditMode ? ('RETAIL_BILLING.PRODUCTS.EDIT' | translate) : ('RETAIL_BILLING.PRODUCTS.CREATE' | translate) }}</h3>
      </ng-template>
    
    
      <!-- Level Selection Section -->
      @if (!isEditMode) {
        <div class="mb-4 p-3 surface-100 border-round">
          <div class="text-900 font-medium mb-2">{{ 'RETAIL_BILLING.PRODUCTS.WHO_IS_THIS_FOR' | translate }}</div>
          <div class="flex gap-2">
            <p-button [label]="'RETAIL_BILLING.PRODUCTS.COMPANY_LEVEL' | translate"
              [outlined]="productForm.get('ownership_level')?.value !== ownershipLevel.Company"
              [text]="productForm.get('ownership_level')?.value !== ownershipLevel.Company"
              (onClick)="selectLevel(ownershipLevel.Company)"
              [class]="productForm.get('ownership_level')?.value === ownershipLevel.Company ? 'p-button-primary' : ''"
              aria-label="company-level-button" />
            <p-button [label]="'RETAIL_BILLING.PRODUCTS.CURRENT_TENANT' | translate"
              [outlined]="productForm.get('ownership_level')?.value !== ownershipLevel.Tenant"
              [text]="productForm.get('ownership_level')?.value !== ownershipLevel.Tenant"
              (onClick)="selectLevel(ownershipLevel.Tenant)"
              [class]="productForm.get('ownership_level')?.value === ownershipLevel.Tenant ? 'p-button-primary' : ''"
              aria-label="current-tenant-button" />
          </div>
        </div>
      }
    
      <form [formGroup]="productForm" class="grid">
    
        <!-- Category -->
        <div class="col-12 md:col-6">
          <label for="category">{{ 'RETAIL_BILLING.PRODUCTS.CATEGORY' | translate }}</label>
          <p-select id="category"
            formControlName="category_id"
            [options]="categoryOptions"
            optionLabel="name"
            optionValue="value"
            [placeholder]="'RETAIL_BILLING.PRODUCTS.CATEGORY' | translate"
            class="w-full mt-2"
            aria-label="product-category-dropdown" />
          @if (productForm.get('category_id')?.invalid && productForm.get('category_id')?.touched) {
            <small class="p-error">
              {{ 'RETAIL_BILLING.PRODUCTS.CATEGORY' | translate }} is required
            </small>
          }
        </div>
    
        <!-- Brand -->
        <div class="col-12 md:col-6">
          <label for="brand">{{ 'RETAIL_BILLING.PRODUCTS.BRAND' | translate }}</label>
          <p-select id="brand"
            formControlName="brand_id"
            [options]="brandOptions"
            optionLabel="name"
            optionValue="value"
            [placeholder]="'RETAIL_BILLING.PRODUCTS.BRAND' | translate"
            class="w-full mt-2"
            aria-label="product-brand-dropdown" />
          @if (productForm.get('brand_id')?.invalid && productForm.get('brand_id')?.touched) {
            <small class="p-error">
              {{ 'RETAIL_BILLING.PRODUCTS.BRAND' | translate }} is required
            </small>
          }
        </div>
    
    
    
        <!-- Product Name (Primary Language) -->
        <div class="col-12 md:col-6">
          <label for="name_primary">{{ 'RETAIL_BILLING.PRODUCTS.NAME' | translate }}
            @if (isSecondaryLanguage()) {
              <span>({{ primaryLanguage() }})</span>
            }
            <sup class="text-red-500">*</sup>
          </label>
          <input
            pInputText
            id="name_primary"
            formControlName="name_primary_language"
            class="w-full mt-2"
            data-testid="product-name-primary-input" />
            @if (productForm.get('name_primary_language')?.invalid && productForm.get('name_primary_language')?.touched) {
              <small class="p-error">
                {{ 'RETAIL_BILLING.PRODUCTS.NAME' | translate }} is required
              </small>
            }
          </div>
    
          <!-- Product Name (Secondary Language) -->
          @if (isSecondaryLanguage()) {
            <div class="col-12 md:col-6">
              <label for="name_secondary">{{ 'RETAIL_BILLING.PRODUCTS.NAME' | translate }}
                <span>({{ secondaryLanguage() }})</span>
              </label>
              <input
                pInputText
                id="name_secondary"
                formControlName="name_secondary_language"
                class="w-full mt-2"
                dir="rtl"
                data-testid="product-name-secondary-input" />
                @if (productForm.get('name_secondary_language')?.invalid && productForm.get('name_secondary_language')?.touched) {
                  <small class="p-error">
                    {{ 'RETAIL_BILLING.PRODUCTS.NAME' | translate }} is required
                  </small>
                }
              </div>
            }
    
            <!-- Description (Primary Language) -->
            <div class="col-12 md:col-6">
              <label for="desc_primary">{{ 'RETAIL_BILLING.PRODUCTS.DESCRIPTION' | translate }}
                @if (isSecondaryLanguage()) {
                  <span>({{ primaryLanguage() }})</span>
                }
              </label>
              <textarea
                pInputTextarea
                id="desc_primary"
                formControlName="description_primary_language"
                rows="3"
                class="w-full mt-2"
                data-testid="product-description-primary-input">
              </textarea>
            </div>
    
            <!-- Description (Secondary Language) -->
            @if (isSecondaryLanguage()) {
              <div class="col-12 md:col-6">
                <label for="desc_secondary">{{ 'RETAIL_BILLING.PRODUCTS.DESCRIPTION' | translate }}
                  <span>({{ secondaryLanguage() }})</span>
                </label>
                <textarea
                  pInputTextarea
                  id="desc_secondary"
                  formControlName="description_secondary_language"
                  rows="3"
                  class="w-full mt-2"
                  dir="rtl"
                  data-testid="product-description-secondary-input">
                </textarea>
              </div>
            }
    
            <!-- Tax Group (only for tenant level) -->
            <div class="col-12" data-testid="code-container">
              <label for="tax_group">{{ 'RETAIL_BILLING.PRODUCTS.CODE' | translate }} <sup class="p-error">*</sup></label>
              <input
                pInputText
                id="code"
                formControlName="code"
                class="w-full mt-2"
                data-testid="product-code-input" />
                @if (productForm.get('code')?.invalid && productForm.get('code')?.touched) {
                  <small class="p-error">
                    {{ 'RETAIL_BILLING.PRODUCTS.CODE' | translate }} is required
                  </small>
                }
              </div>
    
              <!-- Tax Group (only for tenant level) -->
              @if (isLevelTenant()) {
                <div class="col-12" data-testid="tax-group-container">
                  <label for="tax_group">{{ 'ADMIN.SERVICES.TAX_GROUP' | translate }} <sup class="p-error">*</sup></label>
                  <p-select
                    id="tax_group"
                    formControlName="tax_group_id"
                    [options]="taxGroupOptions"
                    placeholder="{{ 'ADMIN.SERVICES.SELECT' | translate }}"
                    optionLabel="name_primary_language"
                    filterBy="name_primary_language"
                    styleClass="w-full text-sm mt-2"
                    data-testid="tax-group-dropdown">
                    <ng-template pTemplate="selectedItem">
                      @if (productForm.get('tax_group_id')?.value) {
                        <div class="text-sm">
                          {{ productForm.get('tax_group_id')?.value?.name_primary_language }}
                        </div>
                      }
                    </ng-template>
                    <ng-template let-taxGroup pTemplate="item">
                      <div class="text-sm">{{ taxGroup.name_primary_language }}</div>
                    </ng-template>
                  </p-select>
                  @if (productForm.get('tax_group_id')?.invalid && productForm.get('tax_group_id')?.touched) {
                    <small class="p-error">
                      {{ 'ADMIN.SERVICES.TAX_GROUP' | translate }} is required
                    </small>
                  }
                </div>
              }

              <!-- POS Settings -->
              <div class="col-12">
                <div class="surface-100 border-round p-3">
                  <div class="text-900 font-medium mb-3">{{ 'RETAIL_BILLING.PRODUCTS.POS_SETTINGS' | translate }}</div>
                  <div class="flex flex-column gap-3">
                    <div class="flex align-items-start justify-content-between gap-3">
                      <div class="flex-1">
                        <label for="is_quick_sale" class="font-medium">{{ 'RETAIL_BILLING.PRODUCTS.IS_QUICK_SALE' | translate }}</label>
                        <p class="text-sm text-500 m-0 mt-1">{{ 'RETAIL_BILLING.PRODUCTS.IS_QUICK_SALE_HELP' | translate }}</p>
                      </div>
                      <p-toggleSwitch id="is_quick_sale"
                        formControlName="is_quick_sale"
                        aria-label="is-quick-sale-toggle" />
                    </div>
                    <div class="flex align-items-start justify-content-between gap-3">
                      <div class="flex-1">
                        <label for="is_pos_favourite" class="font-medium">{{ 'RETAIL_BILLING.PRODUCTS.IS_POS_FAVOURITE' | translate }}</label>
                        <p class="text-sm text-500 m-0 mt-1">{{ 'RETAIL_BILLING.PRODUCTS.IS_POS_FAVOURITE_HELP' | translate }}</p>
                      </div>
                      <p-toggleSwitch id="is_pos_favourite"
                        formControlName="is_pos_favourite"
                        aria-label="is-pos-favourite-toggle" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Image Upload -->
              <div class="col-12">
                <label>{{ 'RETAIL_BILLING.PRODUCTS.IMAGE' | translate }}</label>
                <div class="mt-2">
                  <div class="border-2 border-dashed border-300 border-round p-4 text-center"
                    style="min-height: 200px; position: relative;">
    
                    <!-- Show uploaded image if exists -->
                    @if (currentImageUrl.value) {
                      <div class="mb-3">
                        <img [src]="currentImageUrl.value" alt="Product image" class="max-w-full max-h-8rem border-round" />
                        <div class="mt-2">
                          <p-button icon="pi pi-times"
                            [text]="true"
                            [rounded]="true"
                            severity="danger"
                            (onClick)="removeImage()"
                            aria-label="remove-image-button" />
                        </div>
                      </div>
                    }
    
                    <!-- File drop zone -->
                    @if (!currentImageUrl.value) {
                      <div class="mx-auto">
                        <ngx-file-drop
                          class="opacity-0 absolute w-full h-full top-0 left-0 z-4 cursor-pointer"
                          [accept]="'.png,.jpg,.jpeg,.gif'"
                          (onFileDrop)="handleFileSelect($event)"
                          aria-label="file-drop-zone">
                          <ng-template ngx-file-drop-content-tmp let-openFileSelector="openFileSelector">
                            <div (click)="openFileSelector()" class="w-full h-full absolute left-0 top-0" aria-label="file-drop-zone-click"></div>
                          </ng-template>
                        </ngx-file-drop>
                        <i class="fa fa-upload text-600 text-4xl"></i>
                        <p class="text-sm font-bold line-height-4 my-2">
                          {{ 'RETAIL_BILLING.COMMON.DRAG_HERE' | translate }}
                          <span class="text-primary block">{{ 'RETAIL_BILLING.COMMON.BROWSE_FILE' | translate }}</span>
                        </p>
                        <p class="text-xs mb-0 text-500">
                          {{ 'RETAIL_BILLING.COMMON.ACCEPTED_FILE_TYPE' | translate }}: .Jpeg, .PNG, .JPG, .Gif
                        </p>
                      </div>
                    }
                  </div>
                </div>
              </div>
    
              <!-- Attribute Schema Configuration Section -->
              <div class="col-12">
                <div class="mb-4">
                  <!-- Section Header -->
                  <div class="flex align-items-center gap-2 mb-4">
                    <i class="pi pi-cog text-primary text-xl"></i>
                    <h4 class="text-900 font-semibold m-0">{{ 'RETAIL_BILLING.PRODUCTS.ATTRIBUTE_CONFIGURATION' | translate }}</h4>
                    <div class="ml-auto">
                      <p-button label="Add Attribute"
                        icon="pi pi-plus"
                        size="small"
                        (onClick)="addNewAttribute()"
                        aria-label="add-new-attribute-button" />
                    </div>
                  </div>
    
                  <!-- Attributes Configuration List -->
                  @if (attributeSchemas.length > 0) {
                    <div class="space-y-3">
                      @for (attr of attributeSchemas; track trackByAttributeIndex(i, attr); let i = $index) {
                        <div
                          class="surface-card border-1 surface-border border-round-lg p-4">
                          <div class="grid">
                            <!-- Attribute Name -->
                            <div class="col-12 md:col-3">
                              <label [for]="'attr_name_' + i">Attribute Name <sup class="text-red-500">*</sup></label>
                              <input
                                pInputText
                                [id]="'attr_name_' + i"
                                [(ngModel)]="attr.name"
                                [ngModelOptions]="{standalone: true}"
                                placeholder="e.g., size, color"
                                class="w-full mt-2"
                                data-testid="attribute-name-input" />
                              </div>
                              <!-- Attribute Type -->
                              <div class="col-12 md:col-3">
                                <label [for]="'attr_type_' + i">Type <sup class="text-red-500">*</sup></label>
                                <p-select [id]="'attr_type_' + i"
                                  [(ngModel)]="attr.type"
                                  [ngModelOptions]="{standalone: true}"
                                  [options]="ATTRIBUTE_TYPES"
                                  optionLabel="label"
                                  optionValue="value"
                                  placeholder="Select type"
                                  class="w-full mt-2"
                                  (onChange)="onAttributeTypeChange(attr, i)"
                                  data-testid="attribute-type-dropdown" />
                              </div>
                              <!-- Required Toggle -->
                              <div class="col-12 md:col-2">
                                <label [for]="'attr_required_' + i">Required</label>
                                <div class="mt-2">
                                  <p-toggleButton [id]="'attr_required_' + i"
                                    [(ngModel)]="attr.required"
                                    [ngModelOptions]="{standalone: true}"
                                    onLabel="Yes"
                                    offLabel="No"
                                    data-testid="attribute-required-toggle" />
                                </div>
                              </div>
                              <!-- Display Order -->
                              <div class="col-12 md:col-2">
                                <label [for]="'attr_order_' + i">Order</label>
                                <input
                                  pInputText
                                  [id]="'attr_order_' + i"
                                  [(ngModel)]="attr.display_order"
                                  [ngModelOptions]="{standalone: true}"
                                  type="number"
                                  min="1"
                                  placeholder="1"
                                  class="w-full mt-2"
                                  data-testid="attribute-order-input" />
                                </div>
                                <!-- Remove Button -->
                                <div class="col-12 md:col-2">
                                  <label>&nbsp;</label>
                                  <div class="mt-2">
                                    <p-button icon="pi pi-trash"
                                      severity="danger"
                                      [text]="true"
                                      size="small"
                                      (onClick)="removeAttribute(i)"
                                      data-testid="remove-attribute-button" />
                                  </div>
                                </div>
                                <!-- Allowed Values for Enum Type -->
                                @if (attr.type.toLowerCase() === 'enum') {
                                  <div class="col-12">
                                    <label [for]="'attr_values_' + i">Allowed Values <sup class="text-red-500">*</sup></label>
                                    <div class="mt-2">
                                      <p-autocomplete [id]="'attr_values_' + i"
                                        [(ngModel)]="attr.allowed_values"
                                        [ngModelOptions]="{standalone: true}"
                                        [multiple]="true"
                                        [typeahead]="false"
                                        [addOnBlur]="true"
                                        [forceSelection]="false"
                                        placeholder="Type value and press Enter"
                                        class="w-full"
                                        data-testid="attribute-values-chips" />
                                      <small class="text-500 block mt-1">
                                        <i class="pi pi-info-circle mr-1"></i>
                                        Type each value and press Enter to add. Click the × to remove values.
                                      </small>
                                    </div>
                                  </div>
                                }
                              </div>
                            </div>
                          }
                        </div>
                      }
    
                      <!-- Empty State -->
                      @if (attributeSchemas.length === 0) {
                        <div class="text-center py-8">
                          <i class="pi pi-cog text-400 text-6xl mb-3"></i>
                          <h5 class="text-600 mb-2">No attributes configured</h5>
                          <p class="text-500 mb-4">Add attributes to define product characteristics like size, color, weight, etc.</p>
                          <p-button label="Add First Attribute"
                            icon="pi pi-plus"
                            (onClick)="addNewAttribute()"
                            aria-label="add-first-attribute-button" />
                        </div>
                      }
                    </div>
                  </div>
                </form>
    
                <ng-template pTemplate="footer">
                  <div class="flex justify-content-end gap-2">
                    <p-button [label]="'RETAIL_BILLING.COMMON.CANCEL' | translate"
                      icon="pi pi-times"
                      [text]="true"
                      (onClick)="onCancel.emit()"
                      aria-label="cancel-button" />
                    <p-button [label]="'RETAIL_BILLING.COMMON.SAVE' | translate"
                      icon="pi pi-check"
                      [loading]="saving"
                      [disabled]="productForm.invalid"
                      (onClick)="saveProduct()"
                      aria-label="save-product-button" />
                  </div>
                </ng-template>
              </p-dialog>
    `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductFormComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly productService = inject(ProductService);
  private readonly categoryService = inject(ProductCategoryService);
  private readonly brandService = inject(BrandService);
  private readonly taxGroupService = inject(TaxGroupService);
  private readonly toastService = inject(ToastMessageService);
  private readonly translate = inject(TranslateService);
  private readonly dialogService = inject(DialogService);
  private readonly change = inject(ChangeDetectorRef);

  readonly visible = input(false);
  readonly product = input<ProductResponse | null>(null);
  readonly tenantId = input<number>();
  readonly companyLevel = input<boolean>(true);
  readonly primaryLanguage = input.required<string>();
  readonly secondaryLanguage = input.required<string>();
  readonly isSecondaryLanguage = input(false);
  
  readonly visibleChange = output<boolean>();
  readonly onSave = output<ProductResponse>();
  readonly onCancel = output<void>();

  productForm: FormGroup;
  categoryOptions: any[] = [];
  brandOptions: any[] = [];
  taxGroupOptions: any[] = [];
  saving = false;
  currentImageUrl = new BehaviorSubject<string | null>(null);
  /** S3 URL from GET — used to detect unchanged vs removed image on save */
  private originalImageUrl: string | null = null;
  noTax: any;
  private pendingTaxGroupId: number | null | undefined = undefined;
  private loadedTaxGroupsTenantId: number | null = null;
  private taxGroupsLoadingTenantId: number | null = null;
  private readonly destroy$ = new Subject<void>();

  // Attribute Schema Configuration
  attributeSchemas: AttributeSchema[] = [];
  
  // Available attribute types
  readonly ATTRIBUTE_TYPES = [
    { label: 'Dropdown (Enum)', value: 'Enum' },
    { label: 'Toggle (Boolean)', value: 'Boolean' },
    { label: 'Text (String)', value: 'String' },
    { label: 'Number', value: 'Number' },
    { label: 'Date', value: 'Date' }
  ];
  refCropper?: DynamicDialogRef | null;

  get isEditMode(): boolean {
    return !!this.product();
  }

  get ownershipLevel() {
    return OwnershipLevel;
  }

  isLevelTenant(): boolean {
    return this.productForm?.get('ownership_level')?.value === OwnershipLevel.Tenant;
  }

  selectLevel(level: OwnershipLevel) {
    this.productForm.get('ownership_level')?.setValue(level);
    
    // Reset tax group when level changes
    this.productForm.get('tax_group_id')?.setValue(null);
    
    // Load tax groups for tenant level
    if (level === OwnershipLevel.Tenant && this.tenantId()) {
      this.loadTaxGroups(true);
    } else {
      this.loadedTaxGroupsTenantId = null;
      this.taxGroupsLoadingTenantId = null;
      this.taxGroupOptions = [];
    }
    
    // Update form validation
    this.updateFormValidation();
  }

  constructor() {
    this.productForm = this.fb.group({
      name_primary_language: ['', Validators.required],
      name_secondary_language: [''],
      description_primary_language: ['', Validators.required],
      description_secondary_language: [''],
      category_id: [null, Validators.required],
      brand_id: [null, Validators.required],
      is_active: [true],
      is_digital: [false],
      is_quick_sale: [false],
      is_pos_favourite: [false],
      ownership_level: [OwnershipLevel.Company, Validators.required],
      image_url: [''],
      attribute_schema: [null],
      code: ['', Validators.required],
      tax_group_id: [null]
    });
    this.productForm.get('ownership_level')?.setValue(this.companyLevel() ? OwnershipLevel.Company : OwnershipLevel.Tenant);

    effect(() => {
      const visible = this.visible();
      const product = this.product();
      const tenantId = this.tenantId();

      if (!visible) {
        untracked(() => {
          this.loadedTaxGroupsTenantId = null;
          this.taxGroupsLoadingTenantId = null;
        });
        return;
      }

      untracked(() => {
        if (product) {
          this.populateForm();
        } else {
          this.resetForm();
        }
        this.updateFormValidation();
        if (tenantId && this.isLevelTenant()) {
          this.loadTaxGroups();
        }
      });
    });
  }

  ngOnInit() {
    this.setupNoTax();
    this.loadFormData();
    this.updateFormValidation();
  }

  setupNoTax() {
    this.translate.get('ADMIN.SUBSCRIPTION.NOTAX')
      .pipe(takeUntil(this.destroy$))
      .subscribe((translation) => {
        this.noTax = {
          id: 0,
          name_primary_language: translation,
          name_secondary_language: translation,
        };
        if (!this.taxGroupOptions.some((tg: { id: number }) => tg.id === 0)) {
          this.taxGroupOptions.unshift(this.noTax);
        }
        this.bindTaxGroupSelection(this.pendingTaxGroupId);
        this.change.detectChanges();
      });
  }

  updateFormValidation() {
    if (this.productForm) {
      // const nameSecondaryControl = this.productForm.get('name_secondary_language');
      const taxGroupControl = this.productForm.get('tax_group_id');
      // Update tax group validation based on level
      if (this.isLevelTenant()) {
        taxGroupControl?.setValidators([Validators.required]);
      } else {
        taxGroupControl?.clearValidators();
      }
      taxGroupControl?.updateValueAndValidity();
    }
  }

  loadFormData() {
    // Load categories
    this.categoryService.getCompanyCategories({ page: 1, page_size: RETAIL_LOOKUP_PAGE_SIZE })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.categoryOptions = response.items?.map(cat => ({
            name: cat.name_primary_language,
            value: cat.id
          })) || [];
          this.change.detectChanges();
        }
      });

    // Load brands
    this.brandService.getCompanyBrands({ page: 1, page_size: RETAIL_LOOKUP_PAGE_SIZE })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.brandOptions = response.items?.map(brand => ({
            name: brand.name_primary_language,
            value: brand.id
          })) || [];
          this.change.detectChanges();
        }
      });
  }

  loadTaxGroups(forceReload = false) {
    const tenantId = this.tenantId();
    if (!tenantId || !this.isLevelTenant()) {
      return;
    }
    if (
      !forceReload &&
      (this.loadedTaxGroupsTenantId === tenantId || this.taxGroupsLoadingTenantId === tenantId)
    ) {
      if (this.loadedTaxGroupsTenantId === tenantId) {
        this.bindTaxGroupSelection(this.pendingTaxGroupId);
      }
      return;
    }

    this.taxGroupsLoadingTenantId = tenantId;

    this.taxGroupService.getTaxGroups(tenantId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.taxGroupOptions = response.items || [];
          if (this.noTax) {
            this.taxGroupOptions.unshift(this.noTax);
          }
          this.loadedTaxGroupsTenantId = tenantId;
          this.taxGroupsLoadingTenantId = null;
          this.bindTaxGroupSelection(this.pendingTaxGroupId);
          this.change.detectChanges();
        },
        error: (error) => {
          this.loadedTaxGroupsTenantId = null;
          this.taxGroupsLoadingTenantId = null;
          console.error('Error loading tax groups:', error);
        }
      });
  }

  bindTaxGroupSelection(taxGroupId: number | null | undefined): void {
    if (!this.isLevelTenant()) {
      return;
    }
    const control = this.productForm.get('tax_group_id');
    if (taxGroupId === undefined) {
      return;
    }
    if (taxGroupId === 0 || taxGroupId === null) {
      control?.setValue(this.noTax ?? null);
      return;
    }
    const selected = this.taxGroupOptions.find((tg: { id: number }) => tg.id === taxGroupId);
    control?.setValue(selected ?? null);
  }

  populateForm() {
    const product = this.product();
    if (product) {
      this.pendingTaxGroupId = product.tax_group_id;
      this.productForm.patchValue({
        name_primary_language: product.name_primary_language || '',
        name_secondary_language: product.name_secondary_language || '',
        description_primary_language: product.description_primary_language || '',
        description_secondary_language: product.description_secondary_language || '',
        category_id: product.category_id,
        brand_id: product.brand_id,
        is_active: product.is_active,
        is_digital: product.is_digital,
        is_quick_sale: product.is_quick_sale ?? false,
        is_pos_favourite: product.is_pos_favourite ?? false,
        ownership_level: this.companyLevel() ? OwnershipLevel.Company : OwnershipLevel.Tenant,
        image_url: product.image_url,
        code: product.code || ''
      });
      this.bindTaxGroupSelection(this.pendingTaxGroupId);
      this.originalImageUrl = product.image_url || null;
      this.currentImageUrl.next(product.image_url || null);

      // Populate attribute schema if it exists
      if (product.attribute_schema && Array.isArray(product.attribute_schema)) {
        this.attributeSchemas = product.attribute_schema.map((attr: any) => ({
          name: attr.name || '',
          type: attr.type,
          required: attr.required || false,
          allowed_values: attr.allowed_values || [],
          label: attr.label || '',
          display_order: attr.display_order || 1
        }));
      }
    }
  }

  resetForm() {
    this.pendingTaxGroupId = undefined;
    this.originalImageUrl = null;
    this.productForm.reset({
      status: true,
      ownership_level: this.companyLevel() ? OwnershipLevel.Company : OwnershipLevel.Tenant
    });
    this.currentImageUrl.next(null);

    // Reset attribute schemas
    this.attributeSchemas = [];
  }

  handleFileSelect(files: NgxFileDropEntry[]) {
    if (files && files.length > 0) {
      const file = files[0];
      if (file.fileEntry.isFile) {
        const fileEntry = file.fileEntry as FileSystemFileEntry;
        fileEntry.file((uploadedFile: File) => {
          this.processFile(uploadedFile);
        });
      }
    }
  }

  showCropper(imageFile: any) {
    this.refCropper = this.dialogService.open(ImageCropDialogComponent, {
      showHeader: false,
      baseZIndex: 10000,
      width: '70%',
      contentStyle: { 'max-height': '500px', 'max-width': '800px' },
      data: { imageFile },
    });
    if (this.refCropper) {
    this.refCropper.onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((croppedImage: any) => {
        if (croppedImage) {
          this.currentImageUrl.next(croppedImage.image);
          this.productForm.patchValue({ image_url: croppedImage.image });
          this.change.detectChanges();
        }
      });
    }
  }

  processFile(file: File) {
    // Create a preview URL for the image
    const reader = new FileReader();
    reader.onload = (e) => {
      this.currentImageUrl.next(e.target?.result as string);
      this.productForm.patchValue({ image_url: this.currentImageUrl.value });
      this.change.detectChanges();
    };
    reader.readAsDataURL(file);
  }

  removeImage() { 
    this.currentImageUrl.next(null);
    this.productForm.patchValue({ image_url: '' });
  }



  saveProduct() {
    if (this.productForm.valid) {
      this.saving = true;
      const formData = this.productForm.value;

      // Convert attribute schema to API format
      const typeMapping: Record<string, number> = {
        'String': 0,
        'Number': 1,
        'Boolean': 2,
        'Enum': 3,
        'Date': 4
      };

      const attributeSchemaForAPI = this.attributeSchemas.map(attr => ({
        name: attr.name,
        type: typeMapping[attr.type] || 0,
        required: attr.required,
        allowed_values: attr.allowed_values || [],
        label: attr.label || attr.name,
        display_order: attr.display_order
      }));

      // Handle tax group id: pass 0 if not tenant level, otherwise use selected value
      const taxGroupId = this.isLevelTenant() 
        ? (formData.tax_group_id?.id !== undefined ? formData.tax_group_id.id : formData.tax_group_id) 
        : 0;

      const imagePayload = buildRetailImageWritePayload({
        currentImage: this.currentImageUrl.value ?? formData.image_url,
        originalImageUrl: this.originalImageUrl,
        isEdit: this.isEditMode,
      });

      const request: CreateProductRequest | UpdateProductRequest = {
        name_primary_language: formData.name_primary_language || '',
        name_secondary_language: formData.name_secondary_language || '',
        description_primary_language: formData.description_primary_language || '',
        description_secondary_language: formData.description_secondary_language || '',
        category_id: formData.category_id || 0,
        brand_id: formData.brand_id || 0,
        is_active: formData.is_active || true ,
        is_digital: formData.is_digital || false,
        is_quick_sale: formData.is_quick_sale || false,
        is_pos_favourite: formData.is_pos_favourite || false,
        ...imagePayload,
        attribute_schema: attributeSchemaForAPI,
        tax_group_id: taxGroupId,
        code: formData.code || ''
      };
      
      const saveOperation = this.isEditMode
        ? this.companyLevel()
          ? this.productService.updateCompanyProduct(this.product()!.id, request as UpdateProductRequest)
          : this.productService.updateTenantProduct(this.tenantId()!, this.product()!.id, request as UpdateProductRequest)
        : this.companyLevel()
          ? this.productService.createCompanyProduct(request as CreateProductRequest)
          : this.productService.createTenantProduct(this.tenantId()!, request as CreateProductRequest);

      saveOperation
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.saving = false;
            // Create a mock product response for the event
            const savedProduct: ProductResponse = {
              ...this.product(),
              id: this.product()?.id || 0,
              ...request,
              created_at: this.product()?.created_at || new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: 1,
              updated_by: 1,
              company_id: 1
            } as ProductResponse;
            
            this.onSave.emit(savedProduct);
            this.visibleChange.emit(false);
          },
          error: (error: any) => {
            this.saving = false;
            this.toastService.showError(error);
          }
        });
    }
  }

  // Attribute Schema Management Methods
  addNewAttribute() {
    const newAttribute: AttributeSchema = {
      name: '',
      type: 'string',
      required: false,
      allowed_values: [],
      label: '',
      display_order: this.attributeSchemas.length + 1
    };
    this.attributeSchemas.push(newAttribute);
  }

  removeAttribute(index: number) {
    this.attributeSchemas.splice(index, 1);
    // Update display orders
    this.attributeSchemas.forEach((attr, i) => {
      attr.display_order = i + 1;
    });
  }

  onAttributeTypeChange(attr: AttributeSchema, index: number) {
    // Initialize allowed_values for enum type
    if (attr.type.toLowerCase() === 'enum' && (!attr.allowed_values || attr.allowed_values.length === 0)) {
      attr.allowed_values = [];
    } else if (attr.type.toLowerCase() !== 'enum') {
      attr.allowed_values = [];
    }
  }

  trackByAttributeIndex(index: number): number {
    return index;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.loadedTaxGroupsTenantId = null;
    this.taxGroupsLoadingTenantId = null;
    this.visibleChange.emit(false);
  }

}